import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const SettingToggle = ({ label, description, defaultChecked = false }) => (
  <div className="flex items-center justify-between py-4 border-b border-ink-700/50 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-200">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-1 max-w-[250px] md:max-w-md">{description}</p>}
    </div>
    <div className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors ${defaultChecked ? 'bg-aurora' : 'bg-ink-600'}`}>
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${defaultChecked ? 'right-1' : 'left-1'}`} />
    </div>
  </div>
);

const Settings = () => {
  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl w-full mx-auto flex-1 flex flex-col"
      >
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Preferences</h1>
          <p className="text-gray-400 text-sm">Manage your calm space and data privacy.</p>
        </header>

        <div className="space-y-6 flex-1">
          {/* Privacy Section */}
          <section className="glass-panel rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4 text-lilac">
              <SafeIcon name="Shield" className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Privacy & Encryption</h2>
            </div>
            <div className="space-y-2">
              <SettingToggle 
                label="Local-First Storage" 
                description="Keep all journal entries encrypted on this device. No cloud sync."
                defaultChecked={true}
              />
              <SettingToggle 
                label="Biometric Lock" 
                description="Require Face ID / Touch ID to open MoodGrid."
                defaultChecked={false}
              />
            </div>
          </section>

          {/* Interface Section */}
          <section className="glass-panel rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4 text-aurora">
              <SafeIcon name="Monitor" className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Interface (Cyber-Wellness)</h2>
            </div>
            <div className="space-y-2">
              <SettingToggle 
                label="Constellation Background" 
                description="Enable subtle floating particles for a calming effect."
                defaultChecked={true}
              />
              <SettingToggle 
                label="Minimalist Mode" 
                description="Hide analytics and insights, focus only on journaling."
                defaultChecked={false}
              />
            </div>
          </section>
          
          {/* Notifications */}
          <section className="glass-panel rounded-2xl p-6">
             <div className="flex items-center gap-2 mb-4 text-gray-300">
              <SafeIcon name="Bell" className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Gentle Nudges</h2>
            </div>
            <div className="space-y-2">
              <SettingToggle 
                label="Evening Reflection Prompt" 
                description="Receive a soft notification at 8:00 PM."
                defaultChecked={true}
              />
            </div>
          </section>
        </div>

        {/* Footer Credit exactly as requested */}
        <footer className="mt-12 mb-8 text-center">
          <p className="text-xs text-gray-600 font-mono tracking-wider opacity-80">
            Made by LingLink AI and DigLab AI
          </p>
        </footer>
      </motion.div>
    </div>
  );
};

export default Settings;